import React, { ReactNode } from 'react'
import Skeleton from 'react-loading-skeleton'
import { SKELETON_BASE_COLOR, SKELETON_HIGHLIGHT_COLOR } from '../../constants'
import { formatCurrency, formatNumber } from '../../helpers'
import Icon from '../icon'

interface CountCardProps {
  count?: number
  valueSuffix?: string
  text: string
  icon?: string
  isCurrency: boolean
  loading?: boolean
  children?: ReactNode
  onClick?: () => void
  selected?: boolean
  tooltip?: string
  tooltipId?: string
}

const CountCard: React.FC<CountCardProps> = ({
  count,
  valueSuffix,
  text,
  icon,
  isCurrency,
  loading = false,
  children,
  onClick,
  selected = false,
  tooltip,
  tooltipId = 'count-card-tooltip',
}) => {
  if (loading) {
    return (
      <div
        className={`bg-transparent
      p-6 rounded-2xl
      border border-divider-gray
      flex flex-col gap-2 shadow-count-card
      `}
      >
        <Skeleton
          width={'20%'}
          height={26}
          baseColor={SKELETON_BASE_COLOR}
          highlightColor={SKELETON_HIGHLIGHT_COLOR}
          className="!rounded-2xl"
        />

        <Skeleton
          width={'100%'}
          height={34}
          baseColor={SKELETON_BASE_COLOR}
          highlightColor={SKELETON_HIGHLIGHT_COLOR}
          className="!rounded-2xl"
        />

        <Skeleton
          width={'50%'}
          height={22}
          baseColor={SKELETON_BASE_COLOR}
          highlightColor={SKELETON_HIGHLIGHT_COLOR}
          className="!rounded-2xl"
        />
      </div>
    )
  }

  const content = (
    <>
      <div className="flex items-start justify-between flex-1">
        <div className="flex flex-col gap-2 h-full justify-between">
          <div className="flex items-center gap-2">
            <p className="text-base text-sec-black">{text}</p>
            {tooltip ? (
              <span
                className="inline-flex shrink-0 cursor-help text-pry-gray transition-colors hover:text-primary"
                aria-hidden="true"
                data-tooltip-id={tooltipId}
                data-tooltip-content={tooltip}
              >
                <Icon id="info-icon" height={14} width={14} />
              </span>
            ) : null}
          </div>
          <p className="text-2xl font-bold">
            {isCurrency ? formatCurrency(count ?? 0) : formatNumber(count ?? 0)}{' '}
            {valueSuffix}
          </p>
        </div>

        {icon !== undefined && (
          <div className="text-gray-200">
            <Icon id={icon} height={35} width={35} />
          </div>
        )}
      </div>

      {children ? <div className="mt-2"> {children}</div> : undefined}
    </>
  )

  const className = `
      p-6 rounded-2xl
      border flex flex-col shadow-count-card text-left
      ${
        selected
          ? 'border-primary bg-primary/5'
          : 'border-divider-gray bg-transparent'
      }
  `

  return onClick ? (
    <button
      type="button"
      className={`${className} transition-colors hover:border-primary`}
      onClick={onClick}
      aria-pressed={selected}
      aria-label={tooltip ? `${text}. ${tooltip}` : undefined}
    >
      {content}
    </button>
  ) : (
    <div
      className={className}
      aria-label={tooltip ? `${text}. ${tooltip}` : undefined}
      tabIndex={tooltip ? 0 : undefined}
    >
      {content}
    </div>
  )
}

export default CountCard
